import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Wrench, FileText, Eye, CheckCircle2, CheckCircle, ArrowLeft, Download, MapPin,
  AlertCircle, Loader2, Lock, ListChecks, Plus, Trash2, Home,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { apiClient, backendUrl } from "@/lib/api";
import { getBlockNumber } from "@/lib/blockMapping";
import { HOME_TYPES } from "@/lib/formConfigs";
import { fmtMoney, cleanNum } from "@/lib/money";

const ACCENT = "#059669";
const inputCls =
  "h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none focus:border-emerald-500 disabled:bg-slate-50 disabled:text-slate-500";
const Label = ({ children }) => (
  <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">{children}</label>
);
let _hkeySeq = 0;
const newHouse = (init = {}) => ({ key: `h${++_hkeySeq}`, init });

function HouseSection({ hkey, index, init, editId, disabled, canRemove, onRemove, onChange }) {
  const { t } = useLanguage();
  const [houseType, setHouseType] = useState(init?.house_type || "");
  const [houseNumber, setHouseNumber] = useState(init?.house_number || "");
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [selected, setSelected] = useState(init?.item_codes || []);
  const prefillRef = useRef(init?.item_codes || null);

  const block = useMemo(() => (houseType && houseNumber ? getBlockNumber(houseType, houseNumber) : ""), [houseType, houseNumber]);
  const unit = houseType && houseNumber ? `${houseType}-${houseNumber}` : "";

  const load = useCallback(async () => {
    if (!houseType || !houseNumber) { setProgress(null); return; }
    setLoading(true); setErr("");
    try {
      const q = `/mechanic/house-progress?house_type=${houseType}&house_number=${encodeURIComponent(houseNumber)}&unit_number=${encodeURIComponent(unit)}${editId ? `&exclude_estimation_id=${editId}` : ""}`;
      const data = await apiClient.get(q);
      setProgress(data);
      if (prefillRef.current) {
        const avail = new Set(data.items.filter((i) => i.status === "available").map((i) => i.item_code));
        setSelected(prefillRef.current.filter((c) => avail.has(c)));
        prefillRef.current = null;
      } else {
        setSelected((prev) => prev.filter((c) => data.items.some((i) => i.item_code === c && i.status === "available")));
      }
    } catch (e) {
      setProgress(null); setErr(e.message || "Could not load progress");
    } finally { setLoading(false); }
  }, [houseType, houseNumber, unit, editId]);

  useEffect(() => { const id = setTimeout(load, 350); return () => clearTimeout(id); }, [load]);

  const items = progress?.items || [];
  const selItems = items.filter((it) => selected.includes(it.item_code) && it.status === "available");
  const pct = cleanNum(selItems.reduce((s, it) => s + Number(it.percentage || 0), 0));
  const subtotal = cleanNum(selItems.reduce((s, it) => s + Number(it.price || 0), 0));

  useEffect(() => {
    onChange(hkey, { houseType, houseNumber, unit, block, itemCodes: selItems.map((i) => i.item_code), subtotal, pct, valid: !!(houseType && houseNumber && selItems.length) });
  }, [houseType, houseNumber, block, subtotal, pct, selected, progress]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (code) => { if (!disabled) setSelected((p) => (p.includes(code) ? p.filter((c) => c !== code) : [...p, code])); };

  return (
    <div className="mb-5 rounded-2xl border-2 border-slate-200 bg-white p-5" data-testid={`house-section-${index}`}>
      <div className="mb-4 flex items-center gap-2">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg text-sm font-bold text-white" style={{ backgroundColor: ACCENT }}>{index + 1}</div>
        <Home className="h-5 w-5" style={{ color: ACCENT }} />
        <h3 className="flex-1 text-base font-semibold text-slate-900">{t("house_type")} / {t("house_number")}</h3>
        {canRemove && !disabled ? (
          <button type="button" data-testid={`remove-house-${index}`} onClick={() => onRemove(hkey)} className="flex items-center gap-1 rounded-lg border border-red-200 px-2.5 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50">
            <Trash2 className="h-3.5 w-3.5" /> {t("delete")}
          </button>
        ) : null}
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div>
          <Label>{t("house_type")}</Label>
          <div className="flex gap-2">
            {HOME_TYPES.map((ht) => (
              <button key={ht} type="button" data-testid={`house-type-${index}-${ht}`} disabled={disabled} onClick={() => setHouseType(ht)} className={`flex-1 rounded-lg border py-2 text-sm font-semibold ${houseType === ht ? "border-transparent text-white" : "border-slate-200 bg-white text-slate-500"}`} style={houseType === ht ? { backgroundColor: ACCENT } : {}}>{ht}</button>
            ))}
          </div>
        </div>
        <div>
          <Label>{t("house_number")}</Label>
          <input data-testid={`house-number-${index}`} className={inputCls} value={houseNumber} onChange={(e) => setHouseNumber(e.target.value.replace(/[^0-9]/g, ""))} placeholder={t("enter_house_number")} inputMode="numeric" disabled={disabled} />
        </div>
        <div>
          <Label>{t("block_number")}</Label>
          <div className="flex h-10 items-center gap-2 rounded-lg border bg-slate-100 px-3 text-sm" style={{ borderColor: block ? ACCENT : "#E2E8F0" }}>
            {block ? (<><MapPin className="h-4 w-4 text-emerald-600" /><span className="font-semibold text-emerald-600">{block}</span></>) : (<span className="text-slate-400">{t("auto_calculated")}</span>)}
          </div>
        </div>
      </div>

      <div className="mt-4">
        <div className="mb-2 flex items-center gap-2"><ListChecks className="h-4 w-4" style={{ color: ACCENT }} /><span className="text-sm font-semibold text-slate-700">{t("work_items_label")}</span></div>
        {!progress && !loading ? <p className="py-4 text-center text-sm text-slate-400">{err || t("enter_house_first")}</p> : null}
        {loading ? <p className="py-4 text-center text-sm text-slate-400"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></p> : null}
        {progress ? (
          <>
            <div className="mb-3 flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-xs">
              <span className="font-semibold text-slate-600">{t("house_progress")}: <span style={{ color: ACCENT }}>{progress.completed_pct}%</span></span>
              <span className="text-slate-500">{t("base_price_label")}: {fmtMoney(progress.base_price, "USD")}</span>
            </div>
            {progress.is_complete ? (
              <p className="rounded-lg bg-emerald-50 px-3 py-2 text-center text-sm font-semibold text-emerald-700">{t("house_complete")}</p>
            ) : (
              <div className="space-y-1.5">
                {items.map((it) => {
                  const dn = it.status === "completed";
                  const sel = selected.includes(it.item_code);
                  return (
                    <button key={it.item_code} type="button" data-testid={`item-${index}-${it.item_code}`} disabled={dn || disabled} onClick={() => toggle(it.item_code)}
                      className={`flex w-full items-center gap-2.5 rounded-lg border p-2.5 text-start ${dn ? "border-slate-200 bg-slate-50 opacity-70" : sel ? "border-emerald-500 bg-emerald-50" : "border-slate-200 bg-white hover:border-emerald-300"}`}>
                      <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${sel && !dn ? "border-transparent bg-emerald-500 text-white" : "border-slate-300 bg-white text-transparent"}`}>
                        {dn ? <Lock className="h-3 w-3 text-slate-400" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                      </span>
                      <span className="flex-1 text-sm text-slate-800">{it.name_ku}</span>
                      <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-semibold text-slate-500">{it.percentage}%</span>
                      <span className="w-20 text-end text-sm font-semibold" style={{ color: dn ? "#94A3B8" : ACCENT }}>{fmtMoney(it.price, "USD")}</span>
                    </button>
                  );
                })}
              </div>
            )}
            {!progress.is_complete ? (
              <div className="mt-3 flex items-center justify-between rounded-lg bg-emerald-50 px-3 py-2">
                <span className="text-xs font-semibold text-emerald-700">{t("selected_total")} ({pct}%)</span>
                <span className="text-sm font-bold text-emerald-700" data-testid={`house-subtotal-${index}`}>{fmtMoney(subtotal, "USD")}</span>
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
}

export default function MechanicProgressForm({ unitNumber, editId }) {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [estNum, setEstNum] = useState("");
  const [estNumError, setEstNumError] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [subject, setSubject] = useState("");
  const [contractorName, setContractorName] = useState("");
  const [contractorPhone, setContractorPhone] = useState("");
  const [houses, setHouses] = useState([newHouse(unitNumber && unitNumber.includes("-") ? { house_type: unitNumber.split("-")[0], house_number: unitNumber.split("-")[1] } : {})]);
  const dataRef = useRef({});
  const [, force] = useState(0);
  const [loadingEdit, setLoadingEdit] = useState(!!editId);

  const [step, setStep] = useState("form");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [excelUrl, setExcelUrl] = useState("");
  const [done, setDone] = useState(false);
  const disabled = step !== "form";

  useEffect(() => {
    if (!editId) return;
    apiClient.get(`/estimations/${editId}`).then((est) => {
      setEstNum(String(est.estimation_number || ""));
      setSubject(est.subject || "");
      setDate(est.date || new Date().toISOString().split("T")[0]);
      setContractorName(est.contractor_name || "");
      setContractorPhone(est.contractor_phone || "");
      const mh = est.mechanic_houses || [];
      setHouses(mh.length ? mh.map((h) => newHouse({ house_type: h.house_type, house_number: h.house_number, item_codes: h.items.map((i) => i.item_code) })) : [newHouse()]);
    }).catch((e) => setError(e.message)).finally(() => setLoadingEdit(false));
  }, [editId]);

  const onHouseChange = useCallback((key, data) => { dataRef.current[key] = data; force((x) => x + 1); }, []);
  const addHouse = () => setHouses((h) => [...h, newHouse()]);
  const removeHouse = (key) => { delete dataRef.current[key]; setHouses((h) => h.filter((x) => x.key !== key)); };

  const housePayload = () => houses.map((h) => dataRef.current[h.key]).filter(Boolean);
  const grandTotal = cleanNum(housePayload().reduce((s, d) => s + Number(d.subtotal || 0), 0));
  const retention = cleanNum(grandTotal * 0.05);
  const afterRetention = cleanNum(grandTotal - retention);

  const buildBody = () => ({
    category: "mechanic", is_mechanic_progress: true,
    estimation_number: parseInt(estNum, 10), date, subject,
    engineer_name: user?.name || "", contractor_name: contractorName, contractor_phone: contractorPhone,
    houses: housePayload().filter((d) => d.valid).map((d) => ({ house_type: d.houseType, house_number: d.houseNumber, item_codes: d.itemCodes })),
  });

  const validate = async () => {
    setError(""); setEstNumError("");
    const numVal = parseInt(estNum, 10);
    if (!estNum || isNaN(numVal) || numVal <= 0) { setEstNumError(t("estimation_number_required")); return false; }
    if (!subject.trim()) { setError(`${t("subject")} ${t("is_required")}`); return false; }
    const valid = housePayload().filter((d) => d.valid);
    if (valid.length === 0) { setError(t("select_work_items")); return false; }
    if (!editId) {
      try {
        const unit = `${valid[0].houseType}-${valid[0].houseNumber}`;
        const check = await apiClient.get(`/estimations/count?unit_number=${unit}&category=mechanic&estimation_number=${numVal}`);
        if (check.is_valid === false) { setEstNumError(check.error || t("estimation_number_duplicate")); return false; }
      } catch { /* proceed */ }
    }
    return true;
  };

  const handlePreview = async () => {
    if (!(await validate())) return;
    setPreviewLoading(true); setError("");
    try {
      const res = await apiClient.post("/estimations/preview-excel", buildBody());
      if (res.preview_url) { setPreviewUrl(`${backendUrl()}${res.preview_url}`); setStep("previewing"); }
    } catch (e) { setError(e.message || "Preview failed"); } finally { setPreviewLoading(false); }
  };

  const handleConfirm = async () => {
    setSubmitting(true); setError("");
    try {
      let estId = editId;
      if (editId) {
        await apiClient.put(`/estimations/${editId}`, { data: buildBody() });
      } else {
        const created = await apiClient.post("/estimations", { unit_number: housePayload().find((d) => d.valid).unit, data: buildBody() });
        estId = created.id;
      }
      try { const docRes = await apiClient.post(`/estimations/${estId}/generate-documents`, {}); if (docRes.excel_url) setExcelUrl(docRes.excel_url); } catch { /* best-effort */ }
      setStep("confirmed"); setDone(true);
    } catch (e) { setError(e.message || t("failed_submit")); setStep("form"); } finally { setSubmitting(false); }
  };

  if (loadingEdit) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-emerald-600" /></div>;

  return (
    <div className="mx-auto max-w-4xl pb-10">
      <div className="mb-5 flex items-center gap-3 rounded-2xl border p-5" style={{ backgroundColor: `${ACCENT}0d`, borderColor: `${ACCENT}33` }}>
        <div className="flex h-12 w-12 items-center justify-center rounded-xl" style={{ backgroundColor: `${ACCENT}20` }}><Wrench className="h-6 w-6" style={{ color: ACCENT }} /></div>
        <div><h1 className="text-xl font-bold text-slate-900">{t("mechanic_progress_form")}</h1><p className="text-sm text-slate-500">{t("mechanic_work")}</p></div>
      </div>

      {step === "previewing" ? (
        <div className="mb-5 rounded-2xl border-2 border-emerald-500 bg-emerald-50 p-5">
          <div className="mb-3 flex items-center gap-3"><FileText className="h-6 w-6 text-emerald-600" /><div><div className="text-base font-bold text-emerald-700">{t("preview_ready")}</div><div className="text-xs text-emerald-600">{t("preview_note")}</div></div></div>
          <a href={previewUrl} target="_blank" rel="noreferrer" data-testid="download-preview-btn" className="mb-3 flex items-center justify-center gap-2 rounded-xl bg-emerald-600 py-3 text-sm font-semibold text-white"><Download className="h-4 w-4" /> {t("download_excel")}</a>
          <div className="flex gap-3">
            <button onClick={() => { setStep("form"); setPreviewUrl(""); }} className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border-2 py-3 text-sm font-semibold" style={{ borderColor: ACCENT, color: ACCENT }}><ArrowLeft className="h-4 w-4" /> {t("back_to_edit")}</button>
            <button data-testid="mech-confirm-btn" onClick={handleConfirm} disabled={submitting} className="flex flex-[2] items-center justify-center gap-1.5 rounded-xl py-3 text-sm font-semibold text-white disabled:opacity-70" style={{ backgroundColor: ACCENT }}>{submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} {t("confirm_and_submit")}</button>
          </div>
        </div>
      ) : null}

      {done ? (
        <div className="mb-5 flex flex-col items-center rounded-2xl border-2 border-emerald-500 bg-emerald-50 p-8 text-center">
          <CheckCircle className="mb-3 h-14 w-14 text-emerald-500" />
          <div className="text-lg font-bold text-slate-900">{t("success")}</div>
          <div className="mt-1 text-sm text-slate-600">{t("estimation_saved")}</div>
          <div className="mt-5 flex gap-3">
            {excelUrl ? <a href={excelUrl} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold text-white" style={{ backgroundColor: ACCENT }}><Download className="h-4 w-4" /> {t("download_excel")}</a> : null}
            <button onClick={() => navigate(-1)} className="rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-600">{t("ok")}</button>
          </div>
        </div>
      ) : null}

      {!done ? (
        <>
          <div className={`mb-5 rounded-2xl border border-slate-200 bg-white p-6 ${disabled ? "opacity-70" : ""}`}>
            <div className="mb-4 flex items-center gap-2"><FileText className="h-5 w-5" style={{ color: ACCENT }} /><h3 className="text-base font-semibold text-slate-900">{t("top_info")}</h3></div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div><Label>{t("estimation_number")}</Label><input data-testid="estimation-number-input" className={inputCls} style={estNumError ? { borderColor: "#DC2626" } : {}} value={estNum} onChange={(e) => { setEstNum(e.target.value.replace(/[^0-9]/g, "")); setEstNumError(""); }} placeholder={t("enter_estimation_number")} inputMode="numeric" disabled={disabled || !!editId} />{estNumError ? <p className="mt-1 text-xs text-red-600">{estNumError}</p> : null}</div>
              <div><Label>{t("subject")}</Label><input data-testid="subject-input" className={inputCls} value={subject} onChange={(e) => setSubject(e.target.value)} placeholder={t("enter_subject")} disabled={disabled} /></div>
              <div><Label>{t("date_label")}</Label><input type="date" className={inputCls} value={date} onChange={(e) => setDate(e.target.value)} disabled={disabled} /></div>
              <div><Label>{t("contractor_name")}</Label><input className={inputCls} value={contractorName} onChange={(e) => setContractorName(e.target.value)} placeholder={t("enter_contractor_name")} disabled={disabled} /></div>
              <div><Label>{t("contractor_phone")}</Label><input className={inputCls} value={contractorPhone} onChange={(e) => setContractorPhone(e.target.value)} placeholder={t("enter_phone")} disabled={disabled} /></div>
              <div><Label>{t("engineer_name_label")}</Label><div className="flex h-10 items-center rounded-lg border border-slate-200 bg-slate-100 px-3 text-sm text-slate-700">{user?.name || "-"}</div></div>
            </div>
          </div>

          {houses.map((h, i) => (
            <HouseSection key={h.key} hkey={h.key} index={i} init={h.init} editId={editId} disabled={disabled} canRemove={houses.length > 1} onRemove={removeHouse} onChange={onHouseChange} />
          ))}

          {!disabled ? (
            <button type="button" data-testid="add-house-btn" onClick={addHouse} className="mb-5 flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-emerald-300 py-4 text-sm font-semibold text-emerald-600 hover:bg-emerald-50">
              <Plus className="h-5 w-5" /> {t("add_house")}
            </button>
          ) : null}

          <div className="mb-4 rounded-2xl bg-slate-900 p-5 text-white">
            <div className="flex items-center justify-between text-sm"><span className="text-slate-300">{t("total_word")}</span><span className="font-semibold" data-testid="grand-total">{fmtMoney(grandTotal, "USD")}</span></div>
            <div className="mt-1.5 flex items-center justify-between text-sm"><span className="text-slate-300">{t("retention_5")}</span><span className="text-amber-300">- {fmtMoney(retention, "USD")}</span></div>
            <div className="mt-1.5 flex items-center justify-between border-t border-slate-700 pt-2 text-base"><span className="font-semibold">{t("final_after_retention")}</span><span className="font-bold text-emerald-400" data-testid="final-total">{fmtMoney(afterRetention, "USD")}</span></div>
          </div>

          {error ? <div className="mb-4 flex items-center gap-2 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-600"><AlertCircle className="h-4 w-4" /> {error}</div> : null}

          {step === "form" ? (
            <button data-testid="mech-preview-btn" onClick={handlePreview} disabled={previewLoading} className="flex w-full items-center justify-center gap-2 rounded-xl border-2 py-4 text-base font-semibold disabled:opacity-70" style={{ borderColor: ACCENT, color: ACCENT, backgroundColor: `${ACCENT}0a` }}>
              {previewLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Eye className="h-5 w-5" />} {t("preview_estimation")}
            </button>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
